import { useMemo, useEffect, useState } from 'react'
import clsx from 'clsx'
import Avatar from 'react-avatar'
import styles from './style.module.scss'
import http from '../../../../shared/http'
import { TApi, DOCUMENT_TYPES } from '../../../../shared/const'
import Table from '../../../../UI/Table'
import MiniHeader from '../../../../UI/MiniHeader'
import { useTranslation } from 'react-i18next'
import Btn from '../../../../UI/Btn'
import StatusIcon from '../../../../UI/StatusIcon'
import eventEmitter, { types } from '../../../../shared/eventEmitter'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { toast } from 'react-toastify'
import BtnTooltip from '../../../../UI/BtnTooltip'

import { ReactComponent as ChairIcon } from '../../../../assets/icons/office-chair.svg'
import ReactTooltip from 'react-tooltip'
import { askConfirm } from '../../../../shared/confirmDialog'
import { optionLabel } from '../../../../shared/optionList'
import { fullLogOut, invitationValidity } from '../../../../shared/helpers'
import { useAvatarUrls } from '../../../../shared/useAvatarUrls'

const statusClassName = {
	Active: styles.statusActive,
	Verification: styles.statusVerification,
	Registered: styles.statusRegistered
}

function CompanyEmployee(props) {
	const { t, i18n } = useTranslation()
	const [employees, setEmployees] = useState([])
	const [invitedEmployees, setInvitedEmployees] = useState([])
	const [countAll, setCountAll] = useState(0)
	const [page, setPage] = useState(0)
	const [sort, setSort] = useState({ name: 'name', direction: 'asc' })
	const limit = 10
	const [isLoading, setLoading] = useState(false)

	const canEdit = !props.isTeacherStudentView

	const fetchEmployees = async (offset = page * limit) => {
		const [
			{
				data: { rows, count }
			},
			{ data: invitedUsers }
		] = await Promise.all([
			http.get(TApi.STUDENT_GET_EMPLOEES, {
				params: {
					limit,
					offset,
					order: sort.name,
					direction: sort.direction
				}
			}),
			http.get(TApi.STUDENT_GET_INVITED_EMPLOYEES)
		])
		setEmployees(rows)
		setCountAll(count)
		setInvitedEmployees(invitedUsers.map(user => ({ ...user, isInvited: true })))
		ReactTooltip.rebuild()
	}

	useEffect(() => {
		fetchEmployees(page * limit)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, sort])

	const handleSort = (name, direction) => {
		setSort({ name, direction })
	}

	/*
	 * Austreten darf, wer nicht die Geschäftsführung innehat — und bei einer
	 * bereits aktivierten Junior Company nur, solange danach mindestens zwei
	 * Personen bleiben. Dieselbe Regel prüft das Backend noch einmal; hier steht
	 * sie, damit der Knopf nicht in eine Fehlermeldung führt.
	 */
	const leaveBlockedReason = employee => {
		if (!employee || employee.isInvited) return t('Leaving is not possible.')
		if (employee.isCEO) return t('First assign your CEO function to another person!')
		if (props.isActivatedCompany ? countAll <= 2 : countAll <= 1)
			return t('An activated company needs at least two employees.')
		return ''
	}

	/*
	 * Jemand anderen entfernen. Dieselbe Zahl wie beim Austritt: Bei einer
	 * aktivierten Junior Company müssen zwei Personen übrig bleiben.
	 *
	 * Bis 21.08.2026 galt hier allein "die letzte Person nicht" — aus zwei mach
	 * eins ging durch, und die Voraussetzung "Mindestens 2 Schüler:innen als
	 * Mitarbeiter:innen" war bei einer längst aktivierten Company nachträglich
	 * wieder offen. Der Server prüft dieselbe Regel; hier steht sie, damit der
	 * Eintrag im Menü nicht in eine Fehlermeldung führt.
	 *
	 * Für eine offene Einladung gilt sie nicht: Die zählt nicht als
	 * Mitarbeiter:in, und ihr Zurückziehen schließt die Voraussetzung "Keine
	 * offene Einladung" erst.
	 */
	const removeBlockedReason = employee => {
		if (!employee || employee.isInvited) return ''
		if (employee.isCEO) return t('First assign your CEO function to another person!')
		if (props.isActivatedCompany ? countAll <= 2 : countAll <= 1)
			return t('An activated company needs at least two employees.')
		return ''
	}

	const makeCEO = userId => {
		http.put(TApi.STUDENT_MAKE_CEO(userId))
			.then(() => {
				toast.success(t('CEO Updated'))
			})
			.finally(() => {
				fetchEmployees()
			})
	}

	const deleteEmployee = (userId, isInvited) => {
		const url = isInvited ? TApi.STUDENT_DELETE_INVITE_EMPLOYEE(userId) : TApi.STUDENT_DELETE_EMPLOYEE(userId)

		setLoading(true)
		http.delete(url)
			.then(() => {
				toast.success(isInvited ? t('Invitation deleted') : t('Employee successfully deleted'))
				eventEmitter.emit(types.refreshEmployeesNavbar)
			})
			.finally(() => {
				fetchEmployees()
				setLoading(false)
			})
	}

	/*
	 * Selbst austreten. Anders als das Entfernen einer anderen Person endet das
	 * mit dem Abmelden — die Anwendung hat ohne Junior Company nichts mehr zu
	 * zeigen.
	 */
	const handleLeave = async employee => {
		const blocked = leaveBlockedReason(employee)
		if (blocked) return toast.error(blocked)

		const confirmed = await askConfirm({
			title: t('Leave company'),
			text: t('You will be removed from this Junior Company and logged out.'),
			confirmLabel: t('Leave company'),
			cancelLabel: t('Cancel'),
			tone: 'danger'
		})
		if (!confirmed) return

		setLoading(true)
		http.post(TApi.STUDENT_LEAVE_COMPANY)
			.then(() => fullLogOut())
			.finally(() => setLoading(false))
	}

	const handleResendInvite = userId => {
		http.post(TApi.STUDENT_RESEND_INVITE(userId)).then(() => {
			toast.success(t('Invitation resent successfully'))
			fetchEmployees(page * limit)
		})
	}

	const handleDeleteButton = async ({ userId, isCEO, isInvited, employee }) => {
		const blocked = removeBlockedReason(employee || { isCEO, isInvited })
		if (blocked) return toast.error(blocked)

		const confirmed = await askConfirm({
			title: t('Are you sure?'),
			text: isInvited ? t('The invitation will be withdrawn.') : t('Remove employee.'),
			confirmLabel: t('Remove'),
			cancelLabel: t('Cancel'),
			tone: 'danger'
		})

		if (confirmed) deleteEmployee(userId, isInvited)
	}

	const openDetail = employee => {
		eventEmitter.emit(types.openEmployeeDetail, {
			employee,
			canEdit,
			refresh: () => fetchEmployees(page * limit),
			canLeave: !leaveBlockedReason(employee),
			leaveHint: leaveBlockedReason(employee),
			canRemove: !removeBlockedReason(employee),
			removeHint: removeBlockedReason(employee)
		})
	}

	/* Stand eines persönlichen Nachweises (heute: Elternbestätigung). */
	const documentStatus = (row, type, rejected, reviewed) => {
		if (row.isInvited) return { icon: 'warning', label: t('Open'), tone: styles.toneWarn }

		const document = row.Documents && row.Documents.find(item => item.type === type)

		if (document && rejected) return { icon: 'rejected', label: t('Rejected'), tone: styles.toneBad }
		if (document && reviewed) return { icon: 'check', label: t('Checked'), tone: styles.toneGood }
		if (document) return { icon: 'check', label: t('Submitted'), tone: styles.toneNeutral }
		return { icon: 'warning', label: t('Open'), tone: styles.toneWarn }
	}

	const renderDocumentStatus = status => (
		<div className={styles.statusContainer}>
			<span className={clsx(styles.parentalWrapper, styles.status, status.tone)}>
				<StatusIcon name={status.icon} size={18} />
				<span>{status.label}</span>
			</span>
		</div>
	)

	/*
	 * Die Profilbilder aller angezeigten Personen in einem Zug — Einladungen
	 * haben noch keines, sie fallen im Hook stillschweigend weg.
	 */
	const avatarUrls = useAvatarUrls(
		useMemo(() => employees.map(employee => employee.avatarHashName), [employees])
	)

	const columns = useMemo(
		() => [
			{
				accessor: 'actions',
				Cell: ({ row }) => {
					const { isInvited, isCEO, id } = row.original
					if (!canEdit) return null

					if (isInvited)
						return (
							<div>
								<BtnTooltip
									items={[
										{
											label: t('Resend invitation'),
											onClick: () => handleResendInvite(id)
										},
										{
											label: t('Delete invitation'),
											onClick: () =>
												handleDeleteButton({
													isInvited,
													userId: id,
													isCEO
												})
										}
									]}
								/>
							</div>
						)

					const isSelf = id === props.userId

					return (
						<div>
							<BtnTooltip
								items={[
									{
										label: t('Assign managing director (CEO)'),
										onClick: () => makeCEO(id),
										disabled: isCEO
									},
									{
										label: t('Edit function & working area'),
										onClick: () => openDetail(row.original)
									},
									isSelf
										? {
												label: t('Leave company'),
												onClick: () => handleLeave(row.original)
										  }
										: {
												label: t('Delete employee'),
												onClick: () =>
													handleDeleteButton({
														isInvited,
														userId: id,
														isCEO,
														employee: row.original
													}),
												disabled: Boolean(removeBlockedReason(row.original))
										  }
								]}
							/>
						</div>
					)
				},
				width: 10
			},
			{
				// Die Liste beginnt mit dem Menü der Zeile — der Datensatz heisst
				// nach der Person (siehe UI/Table).
				primary: true,
				Header: () => {
					return <span className={styles.tableTitleName}>Name</span>
				},
				minWidth: 250,
				accessor: 'name',
				Cell: ({ row }) => {
					const { isInvited } = row.original
					/*
					 * Bei einer Einladung steht die E-Mail-Adresse als Name da.
					 * Vorher stand dort „Nicht verifiziert" — dieselbe Zeile für
					 * jede Einladung, niemand konnte sehen, wer schon eingeladen
					 * ist, und es wurde ein zweites Mal eingeladen.
					 */
					const fullName = isInvited
						? row.original.email
						: row.original.firstName
						? `${row.original.firstName} ${row.original.lastName}`
						: '-------'
					return (
						<span className={styles.nameContainer}>
							<div
								style={{
									display: 'flex',
									justifyContent: 'space-between',
									width: '100%'
								}}
							>
								<div className={styles.avatarWrapper}>
									{/*
									 * Das Profilbild wird angemeldet geholt und als
									 * lokale Adresse eingesetzt; wer keines hinterlegt
									 * hat — und jede Einladung — bleibt bei den
									 * Initialen.
									 */}
									<Avatar
										className={styles.userAvatar}
										src={avatarUrls[row.original.avatarHashName] || undefined}
										round
										name={fullName}
										size={32}
									/>

									<span className={styles.name}>{fullName}</span>
								</div>
							</div>
						</span>
					)
				}
			},
			{
				Header: t('Function & working area'),
				accessor: 'primaryWorkArea',
				Cell: ({ row }) => {
					const { primaryWorkArea, isInvited } = row.original

					if (isInvited) return <p className={styles.position}>—</p>

					// Die Geschäftsführung steht am Datensatz als eigener Wert, den die
					// Arbeitsbereichsliste nicht kennt — deshalb zuerst.
					if (row.original.isCEO)
						return <p className={styles.position}>{t('Managing director (CEO)')}</p>

					/*
					 * `optionLabel` statt eines eigenen Vergleichs auf `value_EN`:
					 * An älteren Datensätzen steht der Bereich als deutscher Text
					 * (siehe shared/optionList).
					 */
					const text = primaryWorkArea ? (
						// `t(...)` dahinter: Ein Wert, den die Liste nicht kennt, bliebe
						// sonst englisch stehen.
						t(optionLabel(props.workAreas, primaryWorkArea, i18n.language))
					) : (
						<span className={clsx(styles.parentalWrapper, styles.toneWarn)}>
							<StatusIcon
								name="warning"
								size={18}
								title={t('A working area has to be selected. Please assign for a working area')}
							/>
							<span>{t('No working area')}</span>
						</span>
					)

					return <p className={styles.position}>{text}</p>
				}
			},
			{
				Header: t('E-Mail'),
				accessor: 'email',
				Cell: ({ row }) => {
					return <p className={styles.position}>{row.original.email}</p>
				}
			},
			{
				Header: t('phone'),
				accessor: 'phone',
				Cell: ({ row }) => {
					return <p className={styles.phone}>{row.original.phone}</p>
				}
			},

			{
				Header: t('Employee status'),
				accessor: 'status',
				Cell: ({ row }) => {
					if (row.original.isInvited) {
						const validity = invitationValidity(row.original.invitedAt)

						return (
							<span
								className={clsx(
									styles.statusWrapper,
									styles.status,
									validity?.expired ? styles.toneBad : styles.toneWarn
								)}
							>
								<StatusIcon
									name="invited"
									size={18}
									title={t('An invitation has been sent — the account does not exist yet.')}
								/>
								<span className={styles.invitedText}>
									{t('Invited')}
									{!!validity && (
										<span className={styles.validity}>
											{validity.expired
												? t('Expired')
												: t('Valid for {{days}} more days', { days: validity.daysLeft })}
										</span>
									)}
								</span>
							</span>
						)
					}

					return (
						<div className={styles.statusContainer}>
							<span
								className={clsx(
									styles.status,
									row.original.emailConfirmed ? statusClassName.Active : statusClassName.Verification
								)}
							>
								{row.original.emailConfirmed ? t('Active') : t('Verification')}
							</span>
						</div>
					)
				}
			},
			{
				Header: t('Parental Confirmation'),
				Cell: ({ row }) =>
					renderDocumentStatus(
						documentStatus(
							row.original,
							DOCUMENT_TYPES.PARENTAL_CONFIRMATION,
							row.original.rejectedParentalConfirmation,
							row.original.reviewedParentalConfirmation
						)
					)
			},
			{
				Header: '',
				accessor: 'delete',
				width: 40,
				Cell: ({ row }) => {
					if (!canEdit) return null

					const isSelf = !row.original.isInvited && row.original.id === props.userId

					return (
						<Btn
							loading={isLoading}
							iconColor="#000"
							iconSize={{ w: 16, h: 16 }}
							icon={isLoading ? '' : isSelf ? 'logout' : 'delete'}
							theme="white"
							onClick={() =>
								isSelf
									? handleLeave(row.original)
									: handleDeleteButton({
											isInvited: row.original.isInvited,
											userId: row.original.id,
											isCEO: row.original.isCEO
									  })
							}
						/>
					)
				}
			}
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[t, canEdit, countAll, props.userId, page, isLoading, avatarUrls]
	)

	return (
		<div className={styles.root}>
			<MiniHeader className={styles.miniHeader} title={t('Company Employee')} bg="#f8faff">
				{!props.isActivatedCompany && !props.isNeedReview && canEdit && (
					<div className={styles.employeeSection}>
						<div
							className={`${styles.waitlistIcon} ${
								invitedEmployees.length === 0 ? styles.waitlistIconOk : styles.waitlistIconWarn
							}`}
						>
							<ChairIcon width={28} height={28} />
							<span>
								{t('Waitlist')}({invitedEmployees.length})
							</span>
						</div>
						<Btn
							className={styles.addEmployeeBtn}
							icon="plus"
							iconColor="#fff"
							theme="orange"
							onClick={() =>
								eventEmitter.emit(types.openAddEmployeeModal, () => fetchEmployees(page * limit))
							}
						>
							{t('Add employee')}
						</Btn>
					</div>
				)}
			</MiniHeader>
			<div className="container">
				<Table
					columns={columns}
					items={[...invitedEmployees, ...employees]}
					page={page}
					limit={limit}
					countAll={countAll}
					onChange={setPage}
					handleSort={handleSort}
					onRowClick={openDetail}
					rowTitle={t('Show details')}
				/>
			</div>
		</div>
	)
}

CompanyEmployee.propTypes = {
	isActivatedCompany: PropTypes.bool.isRequired,
	isNeedReview: PropTypes.bool.isRequired,
	isTeacherStudentView: PropTypes.bool.isRequired
}

const mapStateToProps = state => ({
	isActivatedCompany: !!state.user.profile.EmploymentCompany?.isActive,
	isNeedReview: !!state.user.profile.EmploymentCompany?.isNeedReview,
	workAreas: state.options.workAreas,
	isTeacherStudentView: state.user.isTeacherStudentView,
	userId: state.user.profile.id
})

export default connect(mapStateToProps)(CompanyEmployee)
